import dayjs, { type Dayjs } from 'dayjs';

const months = [
  { name: 'January', number: '01' },
  { name: 'February', number: '02' },
  { name: 'March', number: '03' },
  { name: 'April', number: '04' },
  { name: 'May', number: '05' },
  { name: 'June', number: '06' },
  { name: 'July', number: '07' },
  { name: 'August', number: '08' },
  { name: 'September', number: '09' },
  { name: 'October', number: '10' },
  { name: 'November', number: '11' },
  { name: 'December', number: '12' },
];

type Month = {
  name: string;
  value: string;
};

export enum DateRangeTypes {
  LAST_DAY = 'Last day',
  LAST_15_DAYS = 'Last 15 days',
  LAST_30_DAYS = 'Last 30 days',
  LAST_QUARTER = 'Last quarter',
  LAST_YEAR = 'Last year',
  MANUAL = 'Manual',
}

interface IDateCheck {
  key: string;
  setDays: (today: Date, days: number, range: DateRangeTypes) => void;
  setQuarter: (today: Date, range: DateRangeTypes) => void;
  setFullYear: (today: Date, range: DateRangeTypes) => void;
  setDateRange: (range: DateRangeTypes) => void;
}

export class DateUtils {
  static dateCheck = (item: IDateCheck) => {
    const date = new Date();
    switch (item.key) {
      case DateRangeTypes.LAST_DAY: {
        item.setDays(date, 1, DateRangeTypes.LAST_DAY);
        break;
      }
      case DateRangeTypes.LAST_15_DAYS: {
        item.setDays(date, 15, DateRangeTypes.LAST_15_DAYS);
        break;
      }
      case DateRangeTypes.LAST_30_DAYS: {
        item.setDays(date, 30, DateRangeTypes.LAST_30_DAYS);
        break;
      }
      case DateRangeTypes.LAST_QUARTER: {
        item.setQuarter(date, DateRangeTypes.LAST_QUARTER);
        break;
      }
      case DateRangeTypes.LAST_YEAR: {
        item.setFullYear(date, DateRangeTypes.LAST_YEAR);
        break;
      }
      case DateRangeTypes.MANUAL: {
        item.setDateRange(DateRangeTypes.MANUAL);
        break;
      }
    }
  };

  static getMonthsSelectList = (minimalMonth: number = 1, maximalMonth: number = 12) => {
    return months
      .map((month) => ({ label: month.name, value: month.number }))
      .filter((m) => {
        return +m.value >= minimalMonth;
      })
      .filter((mnt) => {
        return +mnt.value <= maximalMonth;
      });
  };

  static getLastMonths = () => {
    const today = new Date();
    const dates: Month[] = [];

    const startYear = 2024;
    const startMonth = 0;

    const monthCount = (today.getFullYear() - startYear) * 12 + today.getMonth() - startMonth + 1;

    for (let i = 0; i < monthCount; i++) {
      const d = new Date(startYear, startMonth + i, 2);
      const month = months[d.getMonth()];
      dates.push({
        name: month.name,
        value: this.formatISODate(d)!,
      });
    }

    return dates.reverse();
  };

  static getCurrentMonth = () => {
    const today = new Date();

    const month = months[today.getMonth()].name;

    return month;
  };

  static getYearsList = (startYear: number = 2000) => {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];

    for (let i = startYear; i <= currentYear; i++) {
      yearsArray.push(i);
    }

    return yearsArray.map((e) => e.toString());
  };

  static formatISODate = (date?: Date) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = months[date.getMonth()].number;
    const day = date.getDate();
    return [year.toString(), month, this.pad2(day)].join('-');
  };

  static formatDate = (date: string, options?: { withTime?: boolean; adjustTime?: boolean }) => {
    if (!date) return '';

    let formattedDate = dayjs(date);

    if (options?.adjustTime) {
      formattedDate = formattedDate.subtract(2, 'hour');
    }

    const pattern = `YYYY-MM-DD${options?.withTime ? ' HH:mm' : ''}`;
    return formattedDate.format(pattern);
  };

  static formatToIsoString = (date: Dayjs) => date.utcOffset(0, true).startOf('day').toISOString();

  static getFirstDateOfYear = (year?: string, month?: string): string => {
    return dayjs(`${year}-${month}-01`).startOf('month').utcOffset(0, true).toISOString();
  };

  static getLastDateOfYear = (year?: string, month?: string): string => {
    return dayjs(`${year}-${month}-01`).endOf('month').utcOffset(0, true).toISOString();
  };

  private static pad2 = (value: number) => {
    if (value < 10) return '0' + value.toString();
    return value.toString();
  };
}
